import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useCreateTimeEntry, useLoggableEmployees } from '@/hooks/use-time-entries';
import { toLocalDayKey } from '@/lib/date';
import { extractErrorMessage } from '@/lib/errors';
import { useEmployeeNameFormatter } from '@/lib/employee-name';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  isLogForConfirmed,
  LOG_FOR_SELF,
  logForOptions,
  logForPayload,
  resolveLogFor,
} from '../lib/log-for';
import {
  logSubmission,
  PICK_DATE_MESSAGE,
  readInstant,
  seedLinkedTime,
  type RowState,
} from '../lib/linked-time-fields';
import { useTimeTrackingProjects } from '../lib/time-tracking-projects';
import { FieldError } from './field-error';
import { TimeFieldsRow } from './time-fields-row';

interface ManualEntryFormProps {
  onClose: () => void;
  /**
   * The day the Date field opens on, as a local day key. The Weekly Timesheet
   * passes the day being viewed (or the one whose row's "+" was pressed) so
   * backfilling a past week no longer starts by retyping the date; absent, the
   * form opens on today as before. [HW-868]
   */
  defaultDate?: string;
  defaultProjectId?: string;
  /**
   * The pinned project's name, from whoever pinned it. The task banner needs a
   * label for a project the picker's list may not carry — it offers only active
   * ones — and a dialog that will file hours against a project has to name it.
   * [HW-874]
   */
  projectName?: string | null;
  defaultTaskId?: string;
  taskLabel?: string;
  /**
   * The entry this dialog is a copy of. Seeds the Date, the Description and the
   * whole `Duration | Start | End` row through `seedLinkedTime`, so the copy
   * shows the interval the timesheet row prints rather than a length parked at
   * this form's 09:00 anchor. Saving it creates a new entry. [HW-905]
   */
  duplicateOf?: {
    startedAt: string;
    duration: number;
    description: string | null;
    /** Null on a legacy source, which then seeds as the viewer saw it. [HW-916] */
    timezone: string | null;
  };
}

export function ManualEntryForm({
  onClose,
  defaultDate,
  defaultProjectId,
  projectName,
  defaultTaskId,
  taskLabel,
  duplicateOf,
}: ManualEntryFormProps) {
  const createMutation = useCreateTimeEntry();
  const { data: projectsData } = useTimeTrackingProjects();
  const projects = projectsData?.data ?? [];
  const isTaskContext = !!defaultTaskId && !!defaultProjectId;
  // The banner's colour dot only — its name comes from `projectName`, so a
  // pinned project this list does not carry is still named. Absence from it is
  // NOT read as "this project may not be logged against": it holds active
  // projects only, is narrowed to the caller's own for anyone holding
  // `projects.view.assigned`, and is one page long. Refusing the write is the
  // server's to do. [HW-874] [HW-879]
  const lockedProject = isTaskContext ? projects.find((p) => p.id === defaultProjectId) : null;

  /** A copy read into this form's terms, or null for a new entry. Pinned so the
   *  dialog keeps opening on what it opened on, as Edit Time Entry does. */
  const [seed] = useState(() => (duplicateOf ? seedLinkedTime(duplicateOf) : null));

  const [projectId, setProjectId] = useState(defaultProjectId ?? '');
  /**
   * The chosen project when the picker's list does not carry it, kept as an
   * option of its own so the field names what it will file against. Only a
   * seeded id can be missing — a reader can pick nothing the list did not
   * offer — and only a seeded NAME can name it, so an id arriving without one
   * adds nothing here. Pinned to the SEEDED id as well: if the list refetches
   * without a project the reader has since chosen, pairing that id with the
   * source's name would label one project with another's. [HW-905]
   */
  const missingProject =
    !isTaskContext &&
    projectId &&
    projectId === defaultProjectId &&
    projectName &&
    !projects.some((p) => p.id === projectId)
      ? { id: projectId, name: projectName }
      : null;
  const [description, setDescription] = useState(duplicateOf?.description ?? '');
  // Duration | Start | End, linked. A new entry opens empty (placeholder "0h")
  // with Start pinned at the 09:00 every manual entry has carried, so it stays
  // consistent with the existing ones until the reader sets a real interval.
  // End starts equal to Start. All three are on screen from the outset. [HW-891]
  //
  // A copy opens on the source's three values instead, which marks its length
  // `derived` — so an End edit moves the length here, where on a new entry with
  // a typed Duration it would move the Start. That is `durationSource`, not
  // `durationTyped`, and it is deliberate: a copy answers as Edit does.
  //
  // A sub-minute source would seed an EMPTY Duration and so could not be saved
  // at all; the Weekly Timesheet does not offer a copy of one, which is
  // `canDuplicateEntry`'s second refusal. [HW-891] [HW-905]
  const [row, setRow] = useState<RowState>(() => ({
    linked: seed
      ? seed.state
      : { duration: '', start: '09:00', end: '09:00', durationSource: 'empty' },
    draft: null,
    durationTyped: false,
    rejected: null,
  }));
  const timeState = row.linked;
  // Defaults to the viewer's own calendar day: the UTC reading offered
  // yesterday to anyone east of UTC before 03:00 and tomorrow to anyone west
  // of it from late afternoon. [HW-858]
  const [date, setDate] = useState(() => defaultDate ?? seed?.dateKey ?? toLocalDayKey(new Date()));

  // Whose hours these are. The server decides who may be named at all — and
  // answers for the chosen project, since that is where the authority comes
  // from — so an empty list is what hides the field for most of the company.
  // [HW-864]
  const { data: loggable } = useLoggableEmployees(projectId || undefined);
  const loggableEmployees = loggable ?? [];
  const formatName = useEmployeeNameFormatter();
  const [logFor, setLogFor] = useState(LOG_FOR_SELF);
  const options = logForOptions(loggableEmployees, formatName);
  const logForLabel = options.find((o) => o.value === logFor)?.label ?? null;
  // The selection is only worth sending while the list that offered it is the
  // list on screen. Between choosing a colleague and switching project, the new
  // project's list is in flight and the picker is hidden — the entry must not
  // quietly go to someone the screen has stopped naming. [HW-864]
  const logForConfirmed = isLogForConfirmed(logFor, loggable);

  // A colleague picked on one project must not survive a switch to another:
  // see `resolveLogFor` for what the hours would otherwise do.
  //
  // Keyed on the ANSWER, not on the list rendered from it. Changing the project
  // leaves the new query with no cached data, so `loggable` is briefly
  // undefined; reading that as "nobody" would drop a colleague who is on the
  // new project too — before the list saying so had even arrived.
  useEffect(() => {
    if (!loggable) return;
    setLogFor((current) => resolveLogFor(current, loggable));
  }, [loggable]);

  /**
   * What a click would actually save — the one reading the button and the
   * handler both go by, so the two cannot drift. See `logSubmission`; it settles
   * the row itself, which is what a click on Safari or Firefox for macOS never
   * does on its own. Pure, so computing it here and again in the handler costs
   * nothing. [HW-891]
   */
  const submission = logSubmission(row, date);

  /**
   * A date the browser could not read arrives as an empty string, exactly like
   * a cleared one, and `new Date('T09:00:00')` is an Invalid Date whose
   * `toISOString()` throws. Unguarded, a half-typed year turned Log Time into
   * an uncaught RangeError out of the submit handler. [HW-840]
   */
  const dateIsValid = readInstant(date, timeState.start) !== null;

  /**
   * A refusal from the server names this day and this length — both of which
   * the next keystroke can change — so it goes as soon as the reader acts on
   * it, rather than standing over a form it no longer describes.
   */
  const clearSaveError = () => {
    if (createMutation.isError) createMutation.reset();
  };

  /**
   * Whether this form is still on screen when the answer arrives.
   *
   * It may not be: this dialog is mounted by the task panel and by the
   * timesheet, and either can take it away — the panel closes, the route
   * changes — while a save is in flight. Its own Cancel and Escape are held
   * shut for exactly that reason, but a parent unmounting is not something a
   * child can refuse.
   */
  const mounted = useRef(true);
  useEffect(() => {
    // Set on the way IN as well as cleared on the way out. StrictMode mounts an
    // effect, unmounts it and mounts it again, so a ref only cleared by the
    // cleanup stays false for the rest of the dialog's life — and this one
    // guards the `onClose` on success, which is how a successful save stopped
    // closing the dialog in the dev build while the production one was fine.
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!projectId) return;

    // Read once, through the same function the button judged by. It settles
    // whatever box is still being typed in — a click does not blur it on Safari
    // or on Firefox for macOS — and the settled row goes back on screen whatever
    // the answer, which is what shows a refusal raised by the submit itself. The
    // button is already dead in that case, but a form still submits on Enter
    // from any of its text boxes. [HW-891]
    const { row: settled, payload } = submission;
    if (settled !== row) setRow(settled);
    if (!payload) return;
    const { duration, ...instants } = payload;

    // `mutateAsync`, not `mutate`: a `mutate` callback is not run at all if its
    // observer has gone, and the message below exists only while this dialog
    // does. The global toast is deliberately suppressed for this mutation, so
    // an entry refused after the dialog was taken away would otherwise be lost
    // in silence — which is the one thing this whole change is against.
    // [HW-859]
    createMutation
      .mutateAsync({
        projectId,
        ...logForPayload(logForConfirmed ? logFor : LOG_FOR_SELF),
        taskId: defaultTaskId,
        description: description || undefined,
        ...instants,
        duration,
        source: 'manual',
      })
      .then(() => {
        if (mounted.current) onClose();
      })
      .catch((error: unknown) => {
        if (!mounted.current) {
          toast.error(extractErrorMessage(error, 'This entry could not be saved.'));
        }
      });
  };

  // Closable at any time, including mid-save: the answer is not tied to this
  // dialog staying open, because `handleSubmit` reports from the promise and
  // falls back to a toast when the form has gone. Holding it shut until the
  // request settled was tried and reverted — with no timeout on the API client,
  // a request that never answers would have sealed the dialog with Escape, the
  // overlay and Cancel all inert and nothing to press but reload. [HW-859]
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md overflow-x-hidden">
        <DialogHeader>
          <DialogTitle>Log Time</DialogTitle>
          <DialogDescription>
            {taskLabel
              ? `Log time for ${taskLabel}`
              : 'Manually log time to a project'}
          </DialogDescription>
        </DialogHeader>

        {/*
          `noValidate`: the Duration and time boxes are free text validated by
          `linked-time-fields` for both dialogs, and the edit one has no form to
          hand anything to the browser. Left on, the browser's own bubble would
          fire first here and nowhere else, so the same bad value would be
          explained two different ways depending on which dialog was open. [HW-891]
        */}
        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          {/* Task context banner */}
          {isTaskContext && (lockedProject || projectName) && (
            <div className="flex items-center gap-2 rounded-md bg-accent/50 px-3 py-2 text-sm">
              {lockedProject && (
                <div className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: lockedProject.color }} />
              )}
              <span className="text-muted-foreground">{projectName ?? lockedProject?.name}</span>
              {taskLabel && (
                <>
                  <span className="text-muted-foreground/50">/</span>
                  <span className="font-mono text-primary text-xs">{taskLabel}</span>
                </>
              )}
            </div>
          )}

          {/* Project picker — only when NOT in task context */}
          {!isTaskContext && (
            <div>
              <Label htmlFor="project">Project</Label>
              <Select
                value={projectId}
                onValueChange={(value) => {
                  clearSaveError();
                  setProjectId(value);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select project" />
                </SelectTrigger>
                <SelectContent>
                  {/* The list holds active projects only, one page, narrowed
                      for `projects.view.assigned` — and Radix draws neither a
                      name nor the placeholder for a value with no item, so a
                      copy of hours logged before the project was archived would
                      show an empty box over a live Save. [HW-905] */}
                  {missingProject && (
                    <SelectItem value={missingProject.id}>
                      <div className="flex items-center gap-2">
                        <div className="h-2 w-2 rounded-full bg-muted-foreground/40" />
                        {missingProject.name}
                      </div>
                    </SelectItem>
                  )}
                  {projects.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      <div className="flex items-center gap-2">
                        <div className="h-2 w-2 rounded-full" style={{ backgroundColor: p.color }} />
                        {p.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* "Log for" — shown only to someone the server says may name another
              person on this project: a manager of it, or HR. Everyone else gets
              an empty list and never sees the field. [HW-862] [HW-864] */}
          {loggableEmployees.length > 0 && (
            <div>
              <Label htmlFor="log-for">Log for</Label>
              {/* Cleared here too, and here it matters most: this is the field
                  that changes whose day the standing refusal was about. */}
              <Select
                value={logFor}
                onValueChange={(value) => {
                  clearSaveError();
                  setLogFor(value);
                }}
              >
                <SelectTrigger id="log-for">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {options.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {/* Named out loud, because the mistake this guards against is
                  leaving the picker where the previous entry left it. */}
              {logFor !== LOG_FOR_SELF && logForConfirmed && logForLabel && (
                <p className="mt-1.5 text-xs text-muted-foreground">
                  This entry will belong to {logForLabel}.
                </p>
              )}
            </div>
          )}

          <div className="pb-1">
            <div className="relative">
              <Label htmlFor="date">Date</Label>
              <Input
                id="date"
                type="date"
                value={date}
                onChange={(e) => {
                  clearSaveError();
                  setDate(e.target.value);
                }}
              />
              {!dateIsValid && <FieldError>{PICK_DATE_MESSAGE}</FieldError>}
            </div>
          </div>

          <TimeFieldsRow row={row} onChange={setRow} onEdit={clearSaveError} idPrefix="log" />

          <div>
            <Label htmlFor="description">Description (optional)</Label>
            <Textarea
              id="description"
              placeholder="What did you work on?"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
          </div>

          {/* Why nothing happened, where the button that did nothing is. In the
              flow rather than out of it, unlike the field messages above: this
              one arrives on a press rather than on a keystroke, and it is a
              whole sentence from the server — a 220px strip over the buttons
              would cover them. [HW-859] */}
          {createMutation.isError && (
            <p
              role="alert"
              className="rounded-md bg-destructive/5 px-3 py-2 text-xs leading-snug text-destructive"
            >
              {extractErrorMessage(
                createMutation.error,
                'This entry could not be saved. Please try again.',
              )}
            </p>
          )}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                !projectId || createMutation.isPending || !logForConfirmed || !submission.payload
              }
            >
              {createMutation.isPending ? 'Saving...' : 'Log Time'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
